import { Alert, AlertType } from "@/atoms-react/alert/index.react.tsx";
import { Button } from "@/atoms-react/button/Button.react";
import { Dropdown } from "@/atoms-react/dropdown/index.react";
import InputLabel from "@/atoms-react/input/Input.react";
import { Modal, ModalContent, type ModalProps } from "@/atoms-react/modal/index.react";
import { Base } from "@/atoms-react/text/Text.react.tsx";
import { KYCStatusType } from "@/features/kyc/types.ts";
import { useKypay } from "@/features/kypay/hooks/use-kypay";
import type {
  KypayAccount,
  SearchOlkyPassIdentity,
  TransferKypayAmountProps
} from "@/features/kypay/types.ts";
import { formatCurrencyInput, formatNumber } from "@/features/utils/functions.ts";
import { MagnifyingGlassCircleIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { SolidAuthnModal } from "../../../identity/components/security/solid-authn-modal.react";
enum TransferTypes {
  INTERNAL = "internal",
  EXTERNAL = "external"
}

export const ModalTransferKypay = ({
  accounts,
  ...props
}: ModalProps & {
  accounts: KypayAccount[] | undefined;
}) => {
  const { t } = useTranslation(["wallet"]);
  const [amountString, setAmountString] = useState<string>("0,00");
  const [locked, setLocked] = useState<boolean>(false);
  const [searchKeyword, setSearchKeyword] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<SearchOlkyPassIdentity[]>();
  const [searching, setSearching] = useState<boolean>(false);
  const [displayAuthenticationModal, setDisplayAuthenticationModal] = useState<boolean>(false);
  const [actionData, setActionData] = useState<string | null>(null);
  const [transfer, setTransfer] = useState<TransferKypayAmountProps>({ amount: 0 });
  const [selectedIdentity, setSelectedIdentity] = useState<SearchOlkyPassIdentity | null>(null);
  const [noItemFound, setNoItemFound] = useState<string | null>(null);

  const { useSearchIdentitiesMutation, transferKypayAmountMutation } = useKypay();
  const { mutateAsync: searchIdentities } = useSearchIdentitiesMutation();
  const { mutateAsync: transferKypayAmount, isPending } = transferKypayAmountMutation();
  const resetInputs = () => {
    setAmountString(formatNumber(0));
    onClear();
    setTransfer({ amount: 0 });
  };
  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    forceCursorPosition();
  }, [amountString]);

  const forceCursorPosition = () => {
    if (inputRef.current) {
      const inputLength = inputRef.current.value.length;
      inputRef.current.setSelectionRange(inputLength, inputLength); // Force cursor to the end on focus
    }
  };

  const onChangeSearchKeyword = (value: string) => {
    if (locked) return null;
    setSearchKeyword(value);
    setNoItemFound(null);
  };
  const onInputSearchKeyword = async () => {
    setNoItemFound(null);
    setSearching(true);
    setSuggestions([]);
    const response = await searchIdentities({ keyword: searchKeyword ?? "" });
    if (response.length === 0) {
      setNoItemFound(t("kypay.modal_transfer_kypay.toast.no_item_found"));
    }
    setSearching(false);
    setSuggestions(response);
  };
  const onClickSuffixIcon = async () => {
    if (locked) {
      onClear();
    } else {
      await onInputSearchKeyword();
    }
  };

  const onClear = () => {
    setSearchKeyword("");
    setLocked(false);
    setNoItemFound(null);
    setTransfer((prev) => ({
      ...prev,
      to: undefined
    }));
    setSelectedIdentity(null);
  };
  const getStatusDescription = (status: string) => {
    if (
      status === KYCStatusType.AUTHENTICATED ||
      status === KYCStatusType.AUTHENTICATED_BRONZE ||
      status === KYCStatusType.AUTHENTICATED_SILVER ||
      status === KYCStatusType.AUTHENTICATED_GOLD
    ) {
      return t("kypay.modal_transfer_kypay.input.passport.status_authenticated");
    } else if (status === KYCStatusType.VERIFIED) {
      return t("kypay.modal_transfer_kypay.input.passport.status_verified");
    } else {
      return t("kypay.modal_transfer_kypay.input.passport.status_declarative");
    }
  };
  const getStatus = (identity: SearchOlkyPassIdentity) => {
    const status =
      KYCStatusType[
        identity.olkyPassLevel?.olkypass_status?.english_name
          .toLocaleUpperCase()
          .replace(" ", "_") as keyof typeof KYCStatusType
      ];
    return status;
  };
  const handleExecuteTransfer = async () => {
    handleCloseAuthModal();
    try {
      await transferKypayAmount(transfer);
      resetInputs();
      props.onClose?.();
      toast.success(t("kypay.modal_transfer_kypay.toast.success_transfer"));
    } catch (_) {
      toast.error(t("kypay.modal_transfer_kypay.toast.error_transfer"));
    }
  };
  const handleCloseAuthModal = () => {
    setDisplayAuthenticationModal(false);
  };
  return (
    <>
      <Modal
        className="w-full max-w-lg sm:w-full"
        {...props}
        onClose={() => {
          resetInputs();
          props.onClose?.();
        }}
      >
        <ModalContent title={t("kypay.modal_transfer_kypay.label.header")}>
          <div className={"mt-4 opacity-1"}>
            <InputLabel
              value={amountString}
              onClick={forceCursorPosition}
              onChange={(value: string) => {
                if (value.length > 16) return;

                const formattedValue = formatCurrencyInput(value);
                const parsed = parseFloat(formattedValue);
                if (!isNaN(parsed)) {
                  setTransfer((prev) => ({
                    ...prev,
                    amount: Number((parsed * 100).toFixed(2))
                  }));
                  setAmountString(formatNumber(parsed));
                } else {
                  setAmountString(formatNumber(0));
                }
              }}
              suffixIcon={<span>€</span>}
              type="text"
              label={t("kypay.modal_transfer_fund.input.amount_label")}
              ref={inputRef}
            />

            <InputLabel
              label={t("kypay.modal_transfer_kypay.label.title")}
              required={false}
              className={"mt-6 mb-2"}
              inputComponent={
                <>
                  <Dropdown
                    items={[TransferTypes.INTERNAL, TransferTypes.EXTERNAL].map((o) => ({
                      key: o,
                      value: t("kypay.modal_transfer_kypay.select.source." + o)
                    }))}
                    placeholder={t("kypay.modal_transfer_kypay.select.source.placeholder")}
                    search
                    onChange={({ key }) => {
                      setTransfer((prev) => ({
                        ...prev,
                        type: key
                      }));
                      setTransfer((prev) => ({
                        ...prev,
                        to: undefined
                      }));
                      setSuggestions([]);
                      setSearchKeyword("");
                      setSelectedIdentity(null);
                      setLocked(false);
                    }}
                    initialValue={null}
                  />
                </>
              }
            />

            {transfer?.type === TransferTypes.EXTERNAL && (
              <>
                <InputLabel
                  value={searchKeyword ?? undefined}
                  type="text"
                  placeholder={t("kypay.modal_transfer_kypay.input.placeholder")}
                  className={"mt-3"}
                  onChange={(value) => {
                    onChangeSearchKeyword(value);
                  }}
                  suffixIcon={!locked ? <MagnifyingGlassCircleIcon /> : <XMarkIcon />}
                  onClickSuffixIcon={() => {
                    onClickSuffixIcon();
                  }}
                ></InputLabel>
                {suggestions && suggestions.length > 0 && (
                  <ul className="z-50 mt-1 max-h-40 w-full list-outside overflow-auto rounded-sm border-2 border-slate-50 bg-white font-semibold text-black duration-300 placeholder:font-semibold placeholder:text-slate-400 hover:list-inside hover:border-slate-100 focus:border-primary focus:bg-white dark:border-slate-800 dark:bg-slate-900 dark:text-white dark:focus:border-primary dark:focus:bg-slate-900 dark:hover:border-slate-800 dark:hover:bg-slate-900 dark:placeholder:text-slate-600">
                    {suggestions.slice(0, 20).map((item: SearchOlkyPassIdentity) => {
                      const status = getStatus(item);
                      const description = getStatusDescription(status);
                      return (
                        <li
                          onClick={() => {
                            setTransfer((prev) => ({
                              ...prev,
                              to: item.uuid
                            }));
                            setLocked(true);
                            setSuggestions([]);
                            setSelectedIdentity(item);
                          }}
                          key={item.uuid}
                          className="my-1 cursor-pointer px-4 py-4 hover:rounded-sm hover:bg-slate-50 dark:bg-slate-900 dark:hover:bg-slate-600"
                        >
                          <Base>
                            <span className={"font-semibold"}>
                              {item.firstName} {item.lastName}{" "}
                            </span>
                            <span className={"text-sm"}>
                              {t("kypay.modal_transfer_kypay.input.passport.label")}{" "}
                            </span>
                            <span className={"font-semibold"}>{description}</span>
                          </Base>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {searching && (
                  <ul className="z-50 mt-1 max-h-40 w-full list-outside overflow-auto rounded-sm border-2 border-slate-50 bg-white font-semibold text-black duration-300 placeholder:font-semibold placeholder:text-slate-400 hover:list-inside hover:border-slate-100 focus:border-primary focus:bg-white dark:border-slate-800 dark:bg-slate-900 dark:text-white dark:focus:border-primary dark:focus:bg-slate-900 dark:hover:border-slate-800 dark:hover:bg-slate-900 dark:placeholder:text-slate-600">
                    <li className={"my-1 px-4 py-4"}>
                      {t("kypay.modal_transfer_kypay.label.searching")}
                    </li>
                  </ul>
                )}
              </>
            )}
            {transfer?.type === TransferTypes.INTERNAL && (
              <Dropdown
                className={"mt-3"}
                items={
                  accounts
                    ? accounts.map((o) => ({
                        key: o.account_id,
                        value: "KYPAY - " + o.partner_name
                      }))
                    : []
                }
                placeholder={t("kypay.modal_transfer_kypay.select.source.placeholder")}
                search
                onChange={({ key }) => {
                  setTransfer((prev) => ({
                    ...prev,
                    to: key
                  }));
                }}
                initialValue={null}
              />
            )}

            {selectedIdentity && (
              <Alert
                type={
                  [
                    KYCStatusType.AUTHENTICATED,
                    KYCStatusType.AUTHENTICATED_BRONZE,
                    KYCStatusType.AUTHENTICATED_SILVER,
                    KYCStatusType.AUTHENTICATED_GOLD
                  ].includes(getStatus(selectedIdentity))
                    ? AlertType.Success
                    : [KYCStatusType.VERIFIED].includes(getStatus(selectedIdentity))
                      ? AlertType.Warning
                      : AlertType.Danger
                }
                title=""
                className={"mt-4"}
              >
                <Base>
                  <span className={"font-semibold"}>
                    {selectedIdentity.firstName} {selectedIdentity.lastName}{" "}
                  </span>
                  <span className={"text-sm"}>
                    {t("kypay.modal_transfer_kypay.input.passport.label")}{" "}
                  </span>
                  <span className={"font-semibold"}>
                    {getStatusDescription(getStatus(selectedIdentity))}
                  </span>
                </Base>
              </Alert>
            )}
            {noItemFound && (
              <Alert type={AlertType.Info} title="" className={"mt-4"}>
                <Base>
                  <span className={"text-sm"}>{noItemFound}</span>
                </Base>
              </Alert>
            )}
            <Button
              disabled={!transfer?.amount || !amountString || !transfer?.to}
              className="mt-4"
              loading={isPending}
              onClick={async () => {
                if (transfer.amount) {
                  if (transfer?.type === TransferTypes.EXTERNAL) {
                    setActionData(
                      JSON.stringify({
                        firstName: selectedIdentity?.firstName,
                        lastName: selectedIdentity?.lastName,
                        amount: transfer.amount
                      })
                    );
                    setDisplayAuthenticationModal(true);
                  } else {
                    handleExecuteTransfer();
                  }
                }
              }}
            >
              {t("kypay.modal_transfer_kypay.button.transfer")}
            </Button>
          </div>
        </ModalContent>
      </Modal>
      {displayAuthenticationModal && (
        <SolidAuthnModal
          action="transfer"
          actionData={actionData ?? undefined}
          onClose={handleCloseAuthModal}
          onValidated={async () => {
            handleExecuteTransfer();
          }}
        />
      )}
    </>
  );
};
